import React, { useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import BasicDetailsSection from "./BasicDetailsSection";
import AadhaarDetailsSection from "./AadhaarDetailsSection";
import AccountVerificationSection from "./AccountVerificationSection";
import ShopDetailsSection from "./ShopDetailsSection";
import UploadDocumentSection from "./UploadDocumentSection";
import { useNavigate } from "react-router-dom";



const MerchantOnboard = () => {
    const navigate = useNavigate();
    const userId = useSelector(
        (state) => state.auth?.employeeDetails?.userId
    );
    const role = useSelector(
        (state) => state.auth?.employeeDetails?.role
    );

    const parentId = userId;

    const [basicDetails, setBasicDetails] = useState(null);
    const [aadhaarDetails, setAadhaarDetails] = useState(null);
    const [bankDetails, setBankDetails] = useState(null);
    const [shopDetails, setShopDetails] = useState(null);
    const [documents, setDocuments] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const [loading, setLoading] = useState(false);
    const [popup, setPopup] = useState({
        show: false,
        message: "",
        type: "success",
        redirectTo: null,
    });


    const { callApi } = useApi(
        `/staff/onboard/signup${parentId ? `?parentId=${parentId}` : ""}`,
        "POST"
    );

    const { callApi: callWalletApi } = useApi(
        "/business/wallet/create/v1",
        "POST"
    );

    const { callApi: callAddAgentApi } = useApi(
        "/bpay/aeps/eazypay/add-agent",
        "POST"
    );

    /* ---------------- SUBMIT ---------------- */

    const handleSubmit = async () => {
        if (!basicDetails || !aadhaarDetails) {
            setPopup({
                show: true,
                message: "Please save Basic & Aadhaar details first",
                type: "error",
            });
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();

            /* ---------- MERCHANT DATA ---------- */
            formData.append("role", "MERCHANT");
            formData.append("email", basicDetails.email);
            formData.append("mobileNumber", basicDetails.mobileNumber);
            formData.append("panCard", basicDetails.panNumber);
            formData.append("fullName", basicDetails.fullName);
            formData.append("fatherName", basicDetails.fatherName);
            formData.append("dob", basicDetails.dob);
            formData.append("gender", basicDetails.gender);

            formData.append("aadharNumber", aadhaarDetails.aadhaarNumber);
            formData.append("aadharAddress", aadhaarDetails.houseAddress);
            formData.append("aadharState", aadhaarDetails.state);
            formData.append("aadharDistrict", aadhaarDetails.district);
            formData.append("aadharPinCode", aadhaarDetails.pincode);
            formData.append("landmark", aadhaarDetails.landmark);

            formData.append("accountHolderName", bankDetails.accountHolderName);
            formData.append("accountNumber", bankDetails.accountNumber);
            formData.append("ifscCode", bankDetails.ifscCode);
            formData.append("bankName", bankDetails.bankName);

            formData.append("shopName", shopDetails.shopName);
            formData.append("state", shopDetails.state);
            formData.append("district", shopDetails.district);
            formData.append("landmark", shopDetails.landmark);
            formData.append("pincode", shopDetails.pincode);
            formData.append("shopAddress", shopDetails.shopAddress);
            formData.append("gstNumber", shopDetails.gstNumber);

            formData.append("onboardingStatus", "FULL");
            formData.append("createdBy", userId);

            const fileKeys = [
                "aadhaarBack",
                "aadhaarFront",
                "panCard",
                "passbookOrChequeBook",
                "selfieWithDocuments",
                "selfieWithSalesPerson",
                "shopPhoto",
                "signaturePhoto",
                "gstcertificate",
            ];

            fileKeys.forEach((key) => {
                if (documents?.[key] instanceof File) {
                    formData.append(`kycFiles[${key}]`, documents[key]);
                }
            });

            /* ---------- 1️⃣ CREATE MERCHANT ---------- */
            const response = await callApi(formData);

            if (!response?.user_id || !response?.username) {
                throw new Error(response.message);
            }

            /* ---------- 2️⃣ CREATE WALLET (MANDATORY) ---------- */
            const walletResponse = await callWalletApi({
                mid: response.username,
                business_type: "MERCHANT",
                account_no: bankDetails.accountNumber,
                currency: "INR",
            });

            if (!walletResponse?.message) {
                throw new Error("Wallet creation failed");
            }

            /* ---------- 3️⃣ ADD AGENT (AFTER WALLET SUCCESS) ---------- */

            try {
                await callAddAgentApi({
                    contactPerson: basicDetails.fullName,
                    mobile: basicDetails.mobileNumber,
                    shopAddress: shopDetails.shopAddress,
                    shopName: shopDetails.shopName,
                    panCard: basicDetails.panNumber,
                    aadharCard: aadhaarDetails.aadhaarNumber,
                    dob: basicDetails.dob,
                    mid: response.username,
                });
            } catch (agentErr) {
                console.log("Add Agent Failed:", agentErr);
                // Optional: you can show partial error but not blocking
            }

            /* ---------- 4️⃣ ALL SUCCESS ---------- */
            setPopup({
                show: true,
                message:
                    "Merchant created successfully 🎉\nWallet & Agent created.\nPlease wait for approval.",
                type: "success",
                redirectTo: "/distributordashboard",
            });

        } catch (err) {
            console.error("ONBOARDING ERROR:", err);

            setPopup({
                show: true,
                message:
                    err.message,
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };



    /* ---------------- UI ---------------- */

    return (
        <div className="w-full">
            {/* TOP HEADER (UNCHANGED ✅) */}
            <div className="mb-6 rounded-lg border border-gray-200 bg-white px-6 py-4 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">
                            Merchant Onboarding
                        </h2>
                        <p className="text-xs text-gray-500">
                            Logged-in user information
                        </p>
                    </div>

                    <div className="flex gap-4">
                        <div>
                            <label className="block text-[11px] text-gray-500 mb-1">
                                User ID
                            </label>
                            <input
                                disabled
                                value={userId || ""}
                                className="w-48 rounded-md border bg-gray-100 px-3 py-2 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] text-gray-500 mb-1">
                                Role
                            </label>
                            <input
                                disabled
                                value={role || ""}
                                className="w-40 rounded-md border bg-gray-100 px-3 py-2 text-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTIONS */}
            <div className="space-y-6">
                <BasicDetailsSection
                    onSave={(data) => {
                        setBasicDetails(data);
                        setIsEditing(false);
                    }}
                    onEdit={() => setIsEditing(true)}
                />

                {basicDetails && (
                    <AadhaarDetailsSection
                        onSave={(data) => {
                            setAadhaarDetails(data);
                            setIsEditing(false);
                        }}
                        onEdit={() => setIsEditing(true)}
                    />
                )}

                {basicDetails && aadhaarDetails && (
                    <AccountVerificationSection
                        onSave={(data) => {
                            setBankDetails(data);
                            setIsEditing(false);
                        }}
                        onEdit={() => setIsEditing(true)}
                    />
                )}

                {basicDetails && aadhaarDetails && bankDetails && (
                    <ShopDetailsSection
                        onSave={(data) => {
                            setShopDetails(data);
                            setIsEditing(false);
                        }}
                        onEdit={() => setIsEditing(true)}
                    />
                )}
                {basicDetails && aadhaarDetails && bankDetails && shopDetails && (
                    <UploadDocumentSection
                        onSave={(data) => {
                            setDocuments(data);
                            setIsEditing(false);
                        }}
                        onEdit={() => setIsEditing(true)}
                    />
                )}


            </div>

            {/* FINAL SUBMIT */}
            {basicDetails &&
                aadhaarDetails &&
                bankDetails &&
                shopDetails &&
                documents &&
                !isEditing && (
                    <div className="mt-8 text-right">
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="rounded-full bg-[#0A2A4F] px-8 py-2.5 text-sm text-white"
                        >
                            {loading ? "Submitting..." : "Create Merchant"}
                        </button>
                    </div>
                )}
            {/* POPUP (UNCHANGED ✅) */}
            {popup.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="w-[90%] max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">

                        {/* ICON */}
                        <div className="mb-4 text-4xl">
                            {popup.type === "success" ? "✅" : "❌"}
                        </div>

                        {/* MESSAGE */}
                        <p className="text-lg font-semibold text-gray-800 whitespace-pre-line">
                            {popup.message}
                        </p>

                        {/* OK BUTTON */}
                        <button
                            onClick={() => {
                                setPopup({ show: false, message: "", type: "success", redirectTo: null });

                                if (popup.redirectTo) {
                                    navigate(popup.redirectTo);
                                }
                            }}
                            className="mt-6 rounded-xl bg-indigo-600 px-6 py-2 text-white font-semibold"
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}


        </div>
    );
};

export default MerchantOnboard;
